import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import superAdminTheme from '../../shared/components/data-table/superadmin-theme';
import useStaffCanvasProvisionHook from './useStaffCanvasProvisionHook';

/**
 * Super-admin "Provision to Canvas" action on the staff profile (CC-14598, §8.1).
 *
 * Shown only when the viewer is a super_admin and the profile subject has at
 * least one active FACILITY_ADMIN membership. Renders one of two states:
 *   - "Provisioned to Canvas" chip when every active FACILITY_ADMIN
 *     membership has a non-revoked `lms_admin_membership` row (the staff
 *     is fully provisioned; the button has nothing useful to do).
 *   - Otherwise the provision button + confirmation dialog as before.
 *
 * After a POST the hook performs one snapshot refresh (no polling). Use the
 * adjacent Refresh control or the profile header Refresh once the consumer
 * finishes granting admin on each facility sub-account.
 */
const StaffProvisionToCanvasButton = forwardRef(
  ({ personId, facilityMemberships, isSuperAdmin, disabled }, ref) => {
    const hasFacilityAdminMemberships = Array.isArray(facilityMemberships) && facilityMemberships.length > 0;
    if (!isSuperAdmin || personId == null || personId === '' || !hasFacilityAdminMemberships) {
      return null;
    }
    return (
      <StaffProvisionToCanvasButtonInner
        ref={ref}
        personId={personId}
        facilityMemberships={facilityMemberships}
        disabled={disabled}
      />
    );
  },
);

StaffProvisionToCanvasButton.displayName = 'StaffProvisionToCanvasButton';

const StaffProvisionToCanvasButtonInner = forwardRef(({ personId, facilityMemberships, disabled }, ref) => {
  const [open, setOpen] = useState(false);
  const {
    provision,
    isSubmitting,
    isSuccess,
    error,
    reset,
    refreshCanvasStatus,
    allProvisioned,
    totalFacilities,
    provisionedFacilities,
    statusLoading,
  } = useStaffCanvasProvisionHook(personId);

  useImperativeHandle(
    ref,
    () => ({
      refreshCanvasStatus,
    }),
    [refreshCanvasStatus],
  );

  const handleConfirm = async () => {
    try {
      await provision();
    } catch {
      // error surfaced via hook state / snackbar
    } finally {
      setOpen(false);
    }
  };

  const hasMemberships = (facilityMemberships?.length ?? 0) > 0;

  const refreshControl = (
    <Tooltip title="Refresh Canvas provisioning status">
      <span>
        <IconButton
          size="small"
          aria-label="Refresh Canvas provisioning status"
          onClick={() => refreshCanvasStatus()}
          disabled={disabled || statusLoading}
          data-testid="staff-provision-to-canvas-refresh"
        >
          <RefreshRoundedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderBody = () => {
    if (allProvisioned && totalFacilities > 0) {
      return (
        <>
          <Tooltip
            title={
              totalFacilities === 1
                ? 'This staff member is provisioned as a Canvas facility admin.'
                : `This staff member is provisioned as a Canvas facility admin across all ${totalFacilities} facilities.`
            }
          >
            <Chip
              data-testid="staff-provisioned-to-canvas-chip"
              color="success"
              icon={<CloudDoneRoundedIcon />}
              label="Provisioned to Canvas"
              variant="filled"
            />
          </Tooltip>
          {refreshControl}
        </>
      );
    }

    const partiallyProvisioned =
      totalFacilities > 0 && provisionedFacilities > 0 && provisionedFacilities < totalFacilities;
    const buttonLabel = partiallyProvisioned
      ? `Provision to Canvas (${provisionedFacilities}/${totalFacilities})`
      : 'Provision to Canvas';

    return (
      <>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadRoundedIcon />}
          disabled={disabled || isSubmitting}
          onClick={() => {
            reset();
            setOpen(true);
          }}
          data-testid="staff-provision-to-canvas-button"
        >
          {buttonLabel}
        </Button>
        {refreshControl}

        <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="provision-staff-dialog-title">
          <DialogTitle id="provision-staff-dialog-title">Provision staff to Canvas</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {hasMemberships
                ? 'This will provision the staff member as a Canvas facility admin for the following facilities. This is safe to repeat.'
                : 'This staff member has no active FACILITY_ADMIN memberships. There is nothing to provision.'}
            </DialogContentText>
            {hasMemberships && (
              <List dense>
                {facilityMemberships.map((facility) => (
                  <ListItem key={facility.locationKey} disableGutters>
                    <ListItemText primary={facility.locationName || `Facility ${facility.locationKey}`} />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm} variant="contained" disabled={!hasMemberships || isSubmitting}>
              Provision
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  return (
    <ThemeProvider theme={superAdminTheme}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid="staff-provision-to-canvas-root">
        {renderBody()}
        <Snackbar open={isSuccess} autoHideDuration={4000} onClose={reset}>
          <Alert severity="success" onClose={reset}>
            Provisioning complete. Refresh if the badge has not updated yet.
          </Alert>
        </Snackbar>
        <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={reset}>
          <Alert severity="error" onClose={reset}>
            {error?.message || 'Failed to provision to Canvas.'}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
});

StaffProvisionToCanvasButtonInner.displayName = 'StaffProvisionToCanvasButtonInner';

StaffProvisionToCanvasButton.propTypes = {
  personId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  facilityMemberships: PropTypes.arrayOf(
    PropTypes.shape({
      locationKey: PropTypes.number,
      locationName: PropTypes.string,
    }),
  ),
  isSuperAdmin: PropTypes.bool,
  disabled: PropTypes.bool,
};

StaffProvisionToCanvasButton.defaultProps = {
  facilityMemberships: [],
  isSuperAdmin: false,
  disabled: false,
};

export default StaffProvisionToCanvasButton;
